import type { CleFieldValidationMap } from "@/api/cle-api";

import {
  getCleFieldLabel,
  normalizeFieldKey,
  resolveCleFieldValidationReason,
} from "./cleFieldValidationMessages";

export type CleFieldValidationState = {
  /** `undefined` means the API has no verdict for this field — show no icon. */
  approved: boolean | undefined;
  /** Danish explanation, set only when the field failed. */
  reason: string | null;
};

const NO_VERDICT: CleFieldValidationState = {
  approved: undefined,
  reason: null,
};

/**
 * Read the API's verdict for one field.
 *
 * Deliberately has no local heuristic fallback. Guessing produced red crosses on
 * fields the API never validates (`courseTitle` has no backend rule at all, so
 * an unreadable title showed as failing on a certificate that was approved), and
 * marked every field of manually created evidence as failing, because
 * `fieldValidation` is null when processing_mode is not AI. An absent verdict is
 * now rendered as no icon rather than a fabricated failure.
 */
export const getCleFieldValidationState = (
  fieldValidation: CleFieldValidationMap | null | undefined,
  fieldKey: string,
): CleFieldValidationState => {
  const entry = fieldValidation?.[fieldKey];

  if (!entry) {
    return NO_VERDICT;
  }

  if (entry.approved) {
    return { approved: true, reason: null };
  }

  return {
    approved: false,
    reason: resolveCleFieldValidationReason(fieldKey, entry.reason),
  };
};

/**
 * An `approved: true` verdict on a field the card has no value to print means
 * the response carried the verdict but not the value — the API is older than
 * this client. That is what happened when `courseTime` shipped in this UI
 * first: the verdict is computed from the DB column, so it read `true` while
 * the response omitted the field entirely and the row rendered a green check
 * next to "-". Downgrade it to "no verdict" (no icon) instead of claiming an
 * approval for a value nobody can see.
 *
 * One-directional on purpose: a failing verdict on an empty field is the normal
 * case and keeps its red cross and Danish reason.
 */
export const withApprovalRequiringValue = (
  state: CleFieldValidationState,
  hasValue: boolean,
): CleFieldValidationState =>
  state.approved === true && !hasValue ? NO_VERDICT : state;

/**
 * Failing fields the caller renders no row for.
 *
 * The API enumerates its rules by hand and each view enumerates its rows by
 * hand; nothing reconciles the two, so a rule with no matching row rejected
 * documents invisibly. Surfacing the leftovers closes that gap permanently.
 */
export const getUnmatchedFailingFields = (
  fieldValidation: CleFieldValidationMap | null | undefined,
  renderedFieldKeys: readonly string[],
): { key: string; label: string; reason: string }[] => {
  if (!fieldValidation) {
    return [];
  }

  const rendered = new Set(renderedFieldKeys.map(normalizeFieldKey));

  return Object.entries(fieldValidation)
    .filter(
      ([key, result]) =>
        !result.approved && !rendered.has(normalizeFieldKey(key)),
    )
    .map(([key, result]) => ({
      key,
      label: getCleFieldLabel(key),
      reason: resolveCleFieldValidationReason(key, result.reason),
    }));
};
